/**
 * วิชาตัวเบา on screen: the qinggong tab (every art, how and where it is learned, its levels) and
 * the words on the hotbar's movement buttons, which change with the arts learned. Reads progress
 * and sends `qinggongLearn` / `qinggongTrain`; the rules are in packages/shared/src/qinggong.ts.
 */
import {
  getQinggong,
  QINGGONG,
  QINGGONG_LIST,
  qinggongLearnBlock,
  qinggongMax,
  qinggongTrainBlock,
  qinggongTrainCost,
  ZONES,
  type PlayerCommand,
  type PlayerProgress,
  type QinggongDef,
  type QinggongStats,
  type World,
} from '@nine/shared';
import { currencyIcon, iconEl, qinggongIcon } from '../render/icons';
import { h } from './dom';

const fmt = (n: number) => Math.floor(n).toLocaleString('th-TH');

/** The hotbar is rebuilt when any of this changes (the art in use, move levels, qinggong). */
export const hotbarKey = (pr: PlayerProgress): string =>
  pr.activeArt +
  JSON.stringify(pr.skillLevels) +
  JSON.stringify(pr.qinggong) +
  // Moves taught one at a time (systems/moveLocks.ts) and the arranged bar change the keys too.
  (pr.movesLearned?.length ?? '') +
  JSON.stringify(pr.skillBar ?? null);

/** A hotbar button's tooltip words: title, what it does, and its cost. */
export interface ActionTip {
  title: string;
  body: string;
  cost: string;
}

export function jumpTip(s: QinggongStats): ActionTip {
  const extra = s.maxJumps - 1;
  const air = extra
    ? `กดซ้ำกลางอากาศได้อีก ${extra} ครั้ง${s.maxJumps >= 3 ? ' (ครั้งที่สามคือเหยียบเมฆา)' : ''}`
    : 'ยังกระโดดซ้ำกลางอากาศไม่ได้ (เรียนเท้าผ่านฟ้า)';
  return {
    title: 'กระโดด',
    body: `${air} · ลอยข้ามต้นไม้และวงปราณบนพื้นได้${s.glide ? ' · กระโดดครบแล้วกดอีกครั้งเพื่อร่อนลม' : ''} · โจมตีและใช้กระบวนท่าได้ขณะลอยตัว`,
    cost: extra ? `กระโดดกลางอากาศใช้พลังกาย ${s.airJumpStamina}` : 'ไม่ใช้พลังกาย',
  };
}

/** Null when วิ่งพันลี้ is not learned (the button is left off the bar). */
export function sprintTip(s: QinggongStats): ActionTip | null {
  if (!s.sprint) return null;
  const pct = Math.round((s.sprint.mult - 1) * 100);
  return {
    title: 'วิ่งพันลี้',
    body: `วิชาตัวเบา กด Shift ค้างขณะเดินเพื่อวิ่งเร็วขึ้น ${pct}% (หรือแตะปุ่มนี้เพื่อเปิด-ปิด) ตั้งรับระหว่างวิ่งไม่ได้${s.climb ? ' · วิ่งชนกำแพงเพื่อไต่ขึ้น' : ''}`,
    cost: `ใช้พลังกาย ${s.sprint.stamina} ต่อวินาที`,
  };
}

export function dashTip(s: QinggongStats, learned: boolean): ActionTip {
  return learned
    ? {
        title: getQinggong('swallow-dash').name,
        body: `พุ่งตัว ${s.dash.dist} ช่องไปทางเคอร์เซอร์ หลบได้ทุกการโจมตีขณะพุ่ง รวมถึงวงปราณ${s.dash.air ? ' · พุ่งกลางอากาศได้' : ''}`,
        cost: `พลังกาย ${s.dash.stamina}`,
      }
    : {
        title: 'ก้าวหลบ',
        body: `ก้าวหลบสั้นๆ ${s.dash.dist} ช่อง หลบได้ทุกการโจมตีชั่วครู่ ใช้บนพื้นเท่านั้น (เรียนนกนางแอ่นพุ่งตัวเพื่อพุ่งไกลขึ้น)`,
        cost: `พลังกาย ${s.dash.stamina}`,
      };
}

const HOW: Record<QinggongDef['learn']['how'], string> = {
  teacher: 'ครูสอน',
  book: 'คัมภีร์',
  quest: 'ภารกิจ',
  school: 'สำนัก',
  drop: 'ของตก',
};

function bar(ratio: number): HTMLElement {
  const fill = h('i');
  fill.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
  return h('div', { class: 'bar gold' }, fill);
}

/**
 * The วิชาตัวเบา tab: the arts in a list (learned ones with their level), the picked one on the
 * right — what it does, where it is taught and what it needs, a row per level, and learn / train.
 */
export function qinggongTab(
  world: World,
  command: (c: PlayerCommand) => void,
  picked: string | undefined,
  select: (id: string) => void,
): HTMLElement {
  const pr = world.progress;
  const pickId = QINGGONG[picked ?? ''] ? picked! : QINGGONG_LIST[0]!.id;
  const list = h('nav', { class: 'split-list', ariaLabel: 'รายการวิชาตัวเบา' });
  list.append(h('h3', { class: 'group' }, 'วิชาตัวเบา 輕'));
  for (const q of QINGGONG_LIST) {
    const lv = pr.qinggong[q.id] ?? 0;
    const b = h(
      'button',
      {
        type: 'button',
        class: `li ${q.id === pickId ? 'sel' : ''} ${lv ? '' : 'locked'}`,
        ariaPressed: String(q.id === pickId),
      },
      iconEl(qinggongIcon(q.id), 'ico32 li-ico'),
      h('span', { class: 'li-name' }, q.name),
      h(
        'small',
        { class: `li-state ${lv ? 'on' : ''}` },
        lv ? `ขั้น ${lv}/${qinggongMax(q)}` : 'ยังไม่เรียน',
      ),
    );
    b.addEventListener('click', () => select(q.id));
    list.append(b);
  }

  const q = getQinggong(pickId);
  const lv = pr.qinggong[q.id] ?? 0;
  const max = qinggongMax(q);
  let action: HTMLElement;
  if (!lv) {
    const block = qinggongLearnBlock(pr, q.id, world.map.zone);
    const cost = `${fmt(q.learn.cultivation)} แต้ม${q.learn.gold ? ` · ${fmt(q.learn.gold)} ตำลึง` : ''}`;
    action = h(
      'button',
      { type: 'button', class: 'btn small', disabled: !!block, title: block ?? '' },
      `เรียน · ${cost}`,
    );
    action.addEventListener('click', () => command({ t: 'qinggongLearn', id: q.id }));
  } else if (lv >= max) action = h('span', { class: 'chip on' }, 'ขั้นสูงสุด');
  else {
    const block = qinggongTrainBlock(pr, q.id);
    action = h(
      'button',
      { type: 'button', class: 'btn small', disabled: !!block, title: block ?? '' },
      `ฝึกขั้น ${lv + 1} · ${fmt(qinggongTrainCost(q, lv) ?? 0)}`,
    );
    action.addEventListener('click', () => command({ t: 'qinggongTrain', id: q.id }));
  }

  const L = q.learn;
  const where = [
    `${HOW[L.how]}: ${L.from}`,
    L.zones ? `ที่${L.zones.map((z) => ZONES[z].name).join(' หรือ ')}` : 'เรียนได้ทุกที่',
    ...Object.entries(q.requires ?? {}).map(([id, n]) => `ต้องมี${getQinggong(id).name} ขั้น ${n}`),
  ];
  const block = lv ? null : qinggongLearnBlock(pr, q.id, world.map.zone);
  const detail = h(
    'section',
    { class: `split-detail ${lv ? '' : 'locked'}` },
    h(
      'div',
      { class: 'art-head' },
      iconEl(qinggongIcon(q.id), 'ico64 head-ico'),
      h(
        'div',
        {},
        h('div', { class: 'art-name' }, q.name),
        h('div', { class: 'art-school' }, lv ? `ขั้น ${lv} จาก ${max}` : 'ยังไม่ได้เรียน'),
      ),
      action,
    ),
    h('p', { class: 'note' }, q.desc),
    h('p', { class: 'note' }, where.join(' · ')),
    block ? h('p', { class: 'note' }, h('em', {}, block)) : null,
    lv ? bar(lv / max) : null,
    h('h4', {}, `${max} ขั้น`),
  );
  q.notes.forEach((note, i) => {
    const n = i + 1;
    detail.append(
      h(
        'div',
        { class: 'skill-row' },
        h('span', { class: `glyph ${n <= lv ? 'k-move' : ''}` }, String(n)),
        h('div', { class: 'skill-info' }, h('b', {}, `ขั้น ${n}`), h('small', {}, ` ${note}`)),
        h('small', {}, n <= lv ? 'ได้แล้ว' : n === 1 ? '' : `${fmt(q.train[n - 2] ?? 0)} แต้ม`),
      ),
    );
  });
  return h(
    'div',
    { class: 'tab-arts' },
    h(
      'div',
      { class: 'tab-bar' },
      h(
        'p',
        {},
        'ตัวละครใหม่วิ่ง กระโดด และก้าวหลบได้เพียงพื้นฐาน วิชาตัวเบาเรียนแยกทีละวิชาจากครู คัมภีร์ หรือสำนัก',
      ),
      h(
        'span',
        { class: 'purse' },
        iconEl(currencyIcon('cultivation', 16), 'ico16'),
        'แต้มฝึกปรือ ',
        h('b', {}, fmt(pr.cultivation)),
      ),
    ),
    h('div', { class: 'split' }, list, detail),
  );
}
